import { useRef, useState } from 'react';
import {
  apiErrorText,
  useUnmarkCeoDebtSchedulePaidMutation,
  type CeoDebtScheduleItem,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Dialog } from '@shared/ui/Dialog';
import { formatDateRu, formatMoney } from '../ceoFormat';
import { rowAmounts } from './scheduleModel';
import { DialogError, DialogSummary } from './styles';

interface UnmarkPaidDialogProps {
  item: CeoDebtScheduleItem;
  onClose: () => void;
}

/**
 * Снять отметку оплаты платежа (ошибочная или повторная отметка) — с подтверждением.
 * Оплату, проведённую заявкой Фабрики платежей, сервер не снимет и скажет, какой
 * заявкой: её отменяют в Фабрике. Фокус — на «Отмена», чтобы Enter не снял отметку.
 */
export function UnmarkPaidDialog({ item, onClose }: UnmarkPaidDialogProps) {
  const { paid } = rowAmounts(item);
  const [unmark, { isLoading }] = useUnmarkCeoDebtSchedulePaidMutation();
  const [error, setError] = useState<string | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  async function confirm() {
    if (isLoading) return;
    setError(null);
    try {
      await unmark(item.id).unwrap();
      onClose();
    } catch (err) {
      setError(apiErrorText(err, 'Не удалось снять отметку оплаты.'));
    }
  }

  return (
    <Dialog
      open
      role="alertdialog"
      onClose={() => {
        if (!isLoading) onClose();
      }}
      title="Снять отметку оплаты?"
      initialFocusRef={cancelRef}
      footer={
        <>
          <Button
            ref={cancelRef}
            type="button"
            $variant="secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            Отмена
          </Button>
          <Button type="button" $variant="danger" onClick={() => void confirm()} disabled={isLoading}>
            {isLoading ? 'Снимаю…' : 'Снять отметку'}
          </Button>
        </>
      }
    >
      <DialogSummary>
        Платёж № {item.installment_no} на {formatDateRu(item.due_date)} по договору №{' '}
        {item.contract_number} ({item.creditor_name}): оплачено {formatMoney(paid)}
        {item.paid_date ? ` ${formatDateRu(item.paid_date)}` : ''}.
      </DialogSummary>
      <p>
        Оплаченное станет 0 ₽, платёж снова будет к оплате, остаток долга по договору
        вернётся. Дата оплаты и документ будут очищены.
      </p>
      {error && <DialogError role="alert">{error}</DialogError>}
    </Dialog>
  );
}
